/**
 * Generic sorting utilities for mock data
 * Supports: nested paths, type-aware comparison, configurable direction
 */

import { getNestedValue } from './filters'

export type SortOrder = 'asc' | 'desc' | 'ASC' | 'DESC'

export interface SortOptions {
  /** Field path to sort by (supports dot notation for nested fields) */
  sortBy?: string
  /** Sort direction */
  sortOrder?: SortOrder
}

/**
 * Normalize sort order to lowercase
 */
function normalizeSortOrder(order?: SortOrder): 'asc' | 'desc' {
  if (!order) return 'asc'
  return order.toLowerCase() as 'asc' | 'desc'
}

/**
 * Detect the type of a value for comparison
 */
function detectValueType(value: unknown): 'string' | 'number' | 'date' | 'boolean' | 'unknown' {
  if (value === null || value === undefined) return 'unknown'

  if (typeof value === 'number') return 'number'
  if (typeof value === 'boolean') return 'boolean'

  if (typeof value === 'string') {
    // Check if it's a date string
    if (isDateString(value)) return 'date'
    // Check if it's a numeric string
    if (!Number.isNaN(Number(value)) && value.trim() !== '') return 'number'
    return 'string'
  }

  if (value instanceof Date) return 'date'

  return 'unknown'
}

/**
 * Check if a string looks like a date
 */
function isDateString(value: string): boolean {
  // Common date formats:
  // ISO 8601: 2024-01-15T10:30:00Z or 2024-01-15 10:30:00
  // Date only: 2024-01-15
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO with T
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/, // ISO with space
    /^\d{4}-\d{2}-\d{2}$/ // Date only
  ]

  if (!datePatterns.some(p => p.test(value))) return false

  // Verify it's actually a valid date
  const date = new Date(value)
  return !Number.isNaN(date.getTime())
}

/**
 * Compare two values with type awareness
 */
function compareValues(a: unknown, b: unknown, order: 'asc' | 'desc'): number {
  // Handle nullish values - always sort to end
  if (a === null || a === undefined) return order === 'asc' ? 1 : -1
  if (b === null || b === undefined) return order === 'asc' ? -1 : 1

  const typeA = detectValueType(a)
  const typeB = detectValueType(b)

  // If types are different, compare as strings
  if (typeA !== typeB) {
    const strA = String(a).toLowerCase()
    const strB = String(b).toLowerCase()
    const result = strA.localeCompare(strB)
    return order === 'asc' ? result : -result
  }

  let result: number

  switch (typeA) {
    case 'number': {
      const numA = typeof a === 'number' ? a : Number(a)
      const numB = typeof b === 'number' ? b : Number(b)
      result = numA - numB
      break
    }

    case 'date': {
      const dateA = a instanceof Date ? a : new Date(String(a))
      const dateB = b instanceof Date ? b : new Date(String(b))
      result = dateA.getTime() - dateB.getTime()
      break
    }

    case 'boolean': {
      const boolA = Boolean(a)
      const boolB = Boolean(b)
      result = boolA === boolB ? 0 : boolA ? 1 : -1
      break
    }

    case 'string':
    default: {
      const strA = String(a).toLowerCase()
      const strB = String(b).toLowerCase()
      result = strA.localeCompare(strB)
      break
    }
  }

  return order === 'asc' ? result : -result
}

/**
 * Apply sorting to a dataset
 *
 * @example
 * ```ts
 * // Simple field sorting
 * const sorted = applySorting(transactions, { sortBy: 'amount', sortOrder: 'desc' })
 *
 * // Nested field sorting
 * const sorted = applySorting(transactions, { sortBy: 'customer.name', sortOrder: 'asc' })
 *
 * // Date sorting
 * const sorted = applySorting(transactions, { sortBy: 'createdAt', sortOrder: 'desc' })
 * ```
 */
export function applySorting<T extends object>(
  data: T[],
  options: SortOptions
): T[] {
  const { sortBy, sortOrder } = options

  // If no sort field specified, return original order
  if (!sortBy) return [...data]

  const order = normalizeSortOrder(sortOrder)

  return [...data].sort((a, b) => {
    const valueA = getNestedValue(a, sortBy)
    const valueB = getNestedValue(b, sortBy)
    return compareValues(valueA, valueB, order)
  })
}

/**
 * Apply multiple sort criteria (for secondary sorting)
 *
 * @example
 * ```ts
 * const sorted = applyMultiSorting(transactions, [
 *   { sortBy: 'status', sortOrder: 'asc' },
 *   { sortBy: 'createdAt', sortOrder: 'desc' }
 * ])
 * ```
 */
export function applyMultiSorting<T extends object>(
  data: T[],
  sortCriteria: SortOptions[]
): T[] {
  if (sortCriteria.length === 0) return [...data]

  return [...data].sort((a, b) => {
    for (const { sortBy, sortOrder } of sortCriteria) {
      if (!sortBy) continue

      const valueA = getNestedValue(a, sortBy)
      const valueB = getNestedValue(b, sortBy)
      const order = normalizeSortOrder(sortOrder)
      const result = compareValues(valueA, valueB, order)

      if (result !== 0) return result
    }
    return 0
  })
}

/**
 * Configuration for available sort fields
 */
export interface SortFieldConfig {
  /** Field path */
  field: string
  /** Human-readable label */
  label: string
  /** Default sort order for this field */
  defaultOrder?: SortOrder
}

/**
 * Parse sort parameter from query string
 * Supports formats: "field" or "field:asc" or "field:desc"
 */
export function parseSortParam(sortParam?: string): SortOptions {
  if (!sortParam) return {}

  const parts = sortParam.split(':')
  const sortBy = parts[0]
  const sortOrder = (parts[1] as SortOrder) || 'asc'

  return { sortBy, sortOrder }
}
